import { useState, useMemo, forwardRef, useImperativeHandle } from 'react';
import type { ExerciseJSON, LayoutItem, ValidatedExerciseJSON } from '../../transformers/zod-schemas';
import type { ValidationResult, WidgetValidation } from '../../validators';
import { validate, getMissingInputs, getAnswerableInputs } from '../../validators';
import { LayoutRenderer } from './LayoutRenderer';
import { WidgetRenderer } from '../widgets';
import { ToolsOverlay, ExerciseDndProvider, type ToolConfig } from '../dnd';
import { SoundPlayerProvider, useSoundPlayer, type SoundConfig } from '../sounds';
import { JSON_TYPES, RTL_INLINE_WIDGET_TYPES, RULER_WIDGET_ID, PROTRACTOR_WIDGET_ID, SET_SQUARE_WIDGET_ID } from '../../constants';
import { itemContainsArabic, groupLineItems, type LineSubGroup } from '../utils';
import { normalize } from '../../utils';
import type { UserAnswerValue } from '../types';

// Re-exported so existing consumers that import UserAnswerValue from this file keep working.
export type { UserAnswerValue } from '../types';

import {
  RULER_ASPECT_RATIO,
  PROTRACTOR_ASPECT_RATIO,
  SET_SQUARE_ASPECT_RATIO,
} from '../dnd/movable-tools/geometry-svgs';

// Aspect ratios imported directly from each SVG component — single source of truth.
// If a viewBox ever changes, this map updates automatically.
const TOOL_ASPECT_RATIO: Record<string, number> = {
  ruler:        RULER_ASPECT_RATIO,
  protractor:   PROTRACTOR_ASPECT_RATIO,
  'set-square': SET_SQUARE_ASPECT_RATIO,
};
const FLIP_BTN_EXTRA = 44;   // 36 px button + 8 px gap below set-square

/** Snapshot of how many answerable inputs are currently filled. */
export interface InputCompletion {
  /** Number of widgets the exercise expects an answer for (0 = display-only). */
  total: number;
  /** How many of those currently have a usable value. */
  filled: number;
  /** Widget IDs still empty. */
  missing: string[];
  /** Widget IDs that have a value. */
  filledIds: string[];
  /** true when there is at least one input AND none are missing. */
  isComplete: boolean;
  /** true when at least one input has a value. */
  isAnyFilled: boolean;
}

export interface ExerciseRendererRef {
  submit: () => ValidationResult;
  getValues: () => Record<string, UserAnswerValue>;
  /** Fill status of the answerable inputs — caller decides when to block submit. */
  getCompletion: () => InputCompletion;
  reset: () => void;
  focusFirstInput: () => void;
}

export interface ExerciseRendererProps {
  json: ExerciseJSON;
  onSubmit?: (values: Record<string, UserAnswerValue>) => void;
  onChange?: (values: Record<string, UserAnswerValue>) => void;
  showSubmitButton?: boolean;
  className?: string;
  /** Master on/off for interaction sounds (default true). */
  soundEnabled?: boolean;
  /** Maps sound events → URL or callback. Omitted events stay silent. */
  sounds?: SoundConfig;
}

/** Pre-populate matcher widgets with [] so submit() never receives undefined. */
function buildInitialValues(json: ExerciseJSON): Record<string, UserAnswerValue> {
  const initial: Record<string, UserAnswerValue> = {};
  for (const [id, widget] of Object.entries(json.widgets)) {
    if (widget.type === JSON_TYPES.MATCHER || widget.type === JSON_TYPES.PAIR_MATCHER) initial[id] = [];
  }
  return initial;
}

/**
 * Main renderer component - the contract for rendering exercises.
 *
 * This component is DUMB - it only collects values and passes them up.
 * The parent decides correctness via validation.
 */
export const ExerciseRenderer = forwardRef<ExerciseRendererRef, ExerciseRendererProps>(({
  json,
  onSubmit,
  onChange,
  showSubmitButton = false,
  className = '',
  soundEnabled = true,
  sounds,
}, ref) => {
  const [values, setValues] = useState<Record<string, UserAnswerValue>>(() => buildInitialValues(json));
  const [droppedSources, setDroppedSources] = useState<Map<string, string>>(new Map()); // inputWidgetId → source drag option ID
  const [focusedInputId, setFocusedInputId] = useState<string | null>(null); // Track which input is currently focused
  const [validationDetails, setValidationDetails] = useState<Record<string, WidgetValidation> | null>(null);
  const [resetKey, setResetKey] = useState(0); // Incremented on reset to remount stateful layout items

  // Shared sound player — used by widgets via context AND directly here for
  // submit / answer-feedback sounds (submit() runs outside the provider tree).
  const playSound = useSoundPlayer(soundEnabled, sounds);

  useImperativeHandle(ref, () => ({
    submit: () => {
      // values are already native types (string | string[][]) — no parsing needed.
      const result = validate(json, values);
      playSound(result.isCorrect ? 'answer-correct' : 'answer-wrong');
      const details: Record<string, WidgetValidation> = { ...result.details };

      /** Build a WidgetValidation slot for a fraction sub-field. */
      const slotValidation = (id: string, raw: UserAnswerValue | undefined, expected: string): WidgetValidation => ({
        widgetId: id,
        userValue: typeof raw === 'string' ? raw : undefined,
        isCorrect: typeof raw === 'string' && raw !== '' && normalize(raw) === normalize(expected),
      });

      // Per-slot validation for fraction widgets (x__num, x__den).
      for (const [widgetId, widget] of Object.entries(json.widgets)) {
        if (widget.type !== JSON_TYPES.FRACTION) continue;
        if (!(widgetId in details)) continue;
        const answer = (json as ValidatedExerciseJSON).validation?.answers?.[widgetId] as string | undefined;
        if (!answer) continue;
        const [expectedNum, expectedDen] = answer.split('/', 2);
        if (!expectedNum || !expectedDen) continue;
        if (details[widgetId]?.isCorrect) {
          if (widget.numerator === null) details[`${widgetId}__num`] = { widgetId: `${widgetId}__num`, userValue: undefined, isCorrect: true };
          if (widget.denominator === null) details[`${widgetId}__den`] = { widgetId: `${widgetId}__den`, userValue: undefined, isCorrect: true };
        } else {
          if (widget.numerator === null)   details[`${widgetId}__num`] = slotValidation(`${widgetId}__num`, values[`${widgetId}__num`], expectedNum);
          if (widget.denominator === null) details[`${widgetId}__den`] = slotValidation(`${widgetId}__den`, values[`${widgetId}__den`], expectedDen);
        }
      }

      setValidationDetails(details);
      onSubmit?.(values);
      return result;
    },
    getValues: () => values,
    getCompletion: (): InputCompletion => {
      const answerable = getAnswerableInputs(json);
      const missing = getMissingInputs(json, values);
      const missingSet = new Set(missing);
      const filledIds = answerable.filter((id) => !missingSet.has(id));
      return {
        total: answerable.length,
        filled: filledIds.length,
        missing,
        filledIds,
        isComplete: answerable.length > 0 && missing.length === 0,
        isAnyFilled: filledIds.length > 0,
      };
    },
    reset: () => {
      setValues(buildInitialValues(json));
      setDroppedSources(new Map());
      setFocusedInputId(null);
      setValidationDetails(null);
      setResetKey(k => k + 1);
    },
    focusFirstInput: () => {
      const container = document.querySelector('.math-exercise-renderer');
      const firstInput = container?.querySelector('input[type="text"], input[type="number"], textarea, select');
      if (firstInput instanceof HTMLElement) {
        firstInput.focus();
      }
    },
  }), [json, values, onSubmit, playSound]);

  const handleChange = (widgetId: string, value: UserAnswerValue, fromDrop = false, sourceOptionId?: string) => {
    // Functional updater: multiple onChange calls in the same handler
    // (e.g. re-drag chip between number-line slots) batch correctly.
    setValues(prev => ({ ...prev, [widgetId]: value }));

    // Track which source option ID is placed in which input
    // Only string values can come from drag-and-drop.
    setDroppedSources(prev => {
      const next = new Map(prev);
      if (fromDrop && typeof value === 'string' && value !== '' && sourceOptionId) {
        next.set(widgetId, sourceOptionId);
      } else {
        next.delete(widgetId);
      }
      return next;
    });

    // External callback — best-effort snapshot (may not reflect batched sibling calls)
    onChange?.({ ...values, [widgetId]: value });
  };

  const handleSubmit = () => {
    onSubmit?.(values);
  };

  // Group layout items by line breaks and sub-group text items
  const lineGroups = useMemo(() => {
    // widget-ref items point to inline chips; resolve the actual widget type
    // before checking RTL_INLINE_WIDGET_TYPES, otherwise the check is always false.
    const isRTLInlineWidget = (item: LayoutItem): boolean => {
      if (item.type === JSON_TYPES.WIDGET_REF) {
        const widget = json.widgets[(item as any).widgetId];
        return !!widget && RTL_INLINE_WIDGET_TYPES.has(widget.type);
      }
      return RTL_INLINE_WIDGET_TYPES.has(item.type);
    };

    const groups: Array<{
      items: LayoutItem[];
      hasArabic: boolean;
      hasRTLInlineWidget: boolean;
      subGroups: LineSubGroup[];
    }> = [];
    let currentGroup: LayoutItem[] = [];

    for (const item of json.layout) {

      if (item.type === JSON_TYPES.QUESTION_TEXT) {
        groups.push({
          items: [item],
          hasArabic: itemContainsArabic(item),
          hasRTLInlineWidget: false,
          subGroups: [] // Question text doesn't need sub-grouping
        });
        continue;
      }

      // TABLE is self-contained — flush pending items and push as its own group
      if (item.type === JSON_TYPES.TABLE) {
        if (currentGroup.length > 0) {
          const hasArabic = currentGroup.some(item => itemContainsArabic(item));
          const hasRTLInlineWidget = currentGroup.some(isRTLInlineWidget);
          const subGroups = groupLineItems(currentGroup);
          groups.push({ items: currentGroup, hasArabic, hasRTLInlineWidget, subGroups });
          currentGroup = [];
        }
        groups.push({
          items: [item],
          hasArabic: false,
          hasRTLInlineWidget: false,
          subGroups: []
        });
        continue;
      }

      // NUMBER_LINE is self-contained
      if (item.type === JSON_TYPES.NUMBER_LINE) {
        if (currentGroup.length > 0) {
          const hasArabic = currentGroup.some(item => itemContainsArabic(item));
          const hasRTLInlineWidget = currentGroup.some(isRTLInlineWidget);
          const subGroups = groupLineItems(currentGroup);
          groups.push({ items: currentGroup, hasArabic, hasRTLInlineWidget, subGroups });
          currentGroup = [];
        }
        groups.push({
          items: [item],
          hasArabic: false,
          hasRTLInlineWidget: false,
          subGroups: []
        });
        continue;
      }

      // VERTICAL_OPERATION is self-contained (block-level grid)
      if (item.type === JSON_TYPES.VERTICAL_OPERATION) {
        if (currentGroup.length > 0) {
          const hasArabic = currentGroup.some(item => itemContainsArabic(item));
          const hasRTLInlineWidget = currentGroup.some(isRTLInlineWidget);
          const subGroups = groupLineItems(currentGroup);
          groups.push({ items: currentGroup, hasArabic, hasRTLInlineWidget, subGroups });
          currentGroup = [];
        }
        groups.push({
          items: [item],
          hasArabic: false,
          hasRTLInlineWidget: false,
          subGroups: []
        });
        continue;
      }

      if (item.type === JSON_TYPES.LINEBREAK) {
        if (currentGroup.length > 0) {
          const hasArabic = currentGroup.some(item => itemContainsArabic(item));
          const hasRTLInlineWidget = currentGroup.some(isRTLInlineWidget);
          const subGroups = groupLineItems(currentGroup);
          groups.push({ items: currentGroup, hasArabic, hasRTLInlineWidget, subGroups });
          currentGroup = [];
        }
      } else {
        currentGroup.push(item);
      }
    }

    if (currentGroup.length > 0) {
      const hasArabic = currentGroup.some(item => itemContainsArabic(item));
      const hasRTLInlineWidget = currentGroup.some(isRTLInlineWidget);
      const subGroups = groupLineItems(currentGroup);
      groups.push({ items: currentGroup, hasArabic, hasRTLInlineWidget, subGroups });
    }

    return groups;
  }, [json.layout, json.widgets]);

  // Derive which inputs have dropped values (for LayoutRenderer's isDropped check)
  const droppedInputs = useMemo(() => new Set(droppedSources.keys()), [droppedSources]);

  const dragOptionsWidget = json.widgets['_drag_options'];
  const rulerWidget = json.widgets[RULER_WIDGET_ID];
  const protractorWidget = json.widgets[PROTRACTOR_WIDGET_ID];
  const setSquareWidget = json.widgets[SET_SQUARE_WIDGET_ID];
  const numericKeypadWidget = json.widgets['_numeric_keypad'];

  // Build geometry tool configs from widgets.
  // 'size' from the DSL (e.g. RULER[size=2]) scales the rendered SVG width.
  // Tools are staggered vertically so multiple tools don't fully overlap on load.
  //
  // toolsMinHeight ensures the exercise-layout-container is always tall enough
  // to fully show the tallest tool (absolute elements don't push container height).
  const { tools, toolsMinHeight } = useMemo<{ tools: ToolConfig[]; toolsMinHeight: number }>(() => {
    const list: ToolConfig[] = [];
    const PAD = 12;
    const STAGGER = 24;
    let maxH = 0;

    /** Safely extract numeric size from a geometry widget (defaults to 1). */
    const getSize = (w: unknown): number =>
      typeof w === 'object' && w !== null && 'size' in w && typeof (w as { size: unknown }).size === 'number'
        ? (w as { size: number }).size
        : 1;

    const add = (id: string, type: ToolConfig['type'], width: number) => {
      const offset = list.length * STAGGER;
      list.push({ id, type, width, initialPosition: { x: PAD + offset, y: PAD + offset } });
      const extra = type === 'set-square' ? FLIP_BTN_EXTRA : 0;
      const h = PAD + Math.ceil(width * (TOOL_ASPECT_RATIO[type] ?? 0)) + extra + PAD;
      if (h > maxH) maxH = h;
    };

    if (rulerWidget)     add('ruler-1',      'ruler',       Math.round(getSize(rulerWidget) * 240));
    if (protractorWidget) add('protractor-1', 'protractor',  Math.round(getSize(protractorWidget) * 180));
    if (setSquareWidget)  add('set-square-1', 'set-square',  Math.round(getSize(setSquareWidget) * 160));

    return { tools: list, toolsMinHeight: maxH };
  }, [rulerWidget, protractorWidget, setSquareWidget]);

  return (
    <SoundPlayerProvider play={playSound}>
    <ExerciseDndProvider droppedSources={droppedSources}>
      <div className={`math-exercise-renderer ${className}`}>

        <div
          className={`exercise-layout-container ${numericKeypadWidget ? 'has-keypad' : ''}`}
          style={toolsMinHeight > 0 ? { minHeight: toolsMinHeight } : undefined}
        >
        <div className="exercise-layout">
          {lineGroups.map((group, groupIdx) => (
            <div
              key={groupIdx}
              className="exercise-line"
              dir={group.hasArabic && group.hasRTLInlineWidget ? 'rtl' : undefined}
            >
              {group.subGroups.length > 0 ? (
                // Render sub-groups for regular lines
                group.subGroups.map((subGroup, subIdx) => (
                  subGroup.isTextGroup ? (
                    // Text group - only apply RTL for pure Arabic (no mixed languages)
                    <span key={subIdx} className="text-group" dir={subGroup.shouldApplyRTL ? 'rtl' : undefined}>
                      {subGroup.items.map((item) => (
                        <LayoutRenderer
                          key={item.id}
                          item={item}
                          widgets={json.widgets}
                          values={values}
                          droppedInputs={droppedInputs}
                          validationDetails={validationDetails}
                          onChange={handleChange}
                          onInputFocus={setFocusedInputId}
                          resetKey={resetKey}
                        />
                      ))}
                    </span>
                  ) : (
                    // Widget group - no dir wrapper, render directly
                    subGroup.items.map((item) => (
                      <LayoutRenderer
                        key={item.id}
                        item={item}
                        widgets={json.widgets}
                        values={values}
                        droppedInputs={droppedInputs}
                        validationDetails={validationDetails}
                        onChange={handleChange}
                        onInputFocus={setFocusedInputId}
                        resetKey={resetKey}
                      />
                    ))
                  )
                ))
              ) : (
                // For QUESTION_TEXT items (no sub-groups)
                group.items.map((item) => (
                  <LayoutRenderer
                    key={item.id}
                    item={item}
                    widgets={json.widgets}
                    values={values}
                    droppedInputs={droppedInputs}
                    validationDetails={validationDetails}
                    onChange={handleChange}
                    onInputFocus={setFocusedInputId}
                    resetKey={resetKey}
                  />
                ))
              )}
            </div>
          ))}
        </div>

        {numericKeypadWidget && numericKeypadWidget.type === 'numeric-keypad' && (
          <div className="numeric-keypad-container">
            <WidgetRenderer
              widget={numericKeypadWidget}
              widgetId="_numeric_keypad"
              value={focusedInputId ? (values[focusedInputId] as string || '') : ''}
              onChange={() => {}} // Not used for keypad
              onKeypadChange={(newValue: string) => {
                // Update the focused input directly
                if (focusedInputId) {
                  handleChange(focusedInputId, newValue, false);
                }
              }}
            />
          </div>
        )}

        {/* Geometry tools — absolute overlay anchored to exercise-layout-container.
            All tools start top-left; the student drags each one into position.
            pointer-events:none on the overlay lets clicks through to the exercise. */}
        {tools.length > 0 && (
          <ToolsOverlay tools={tools} resetKey={resetKey} />
        )}
        </div>{/* closes exercise-layout-container */}

        {/* Drag options at the bottom */}
        {dragOptionsWidget && dragOptionsWidget.type === 'drag-options' && (
          <div className="drag-options-container">
            <WidgetRenderer
              widget={dragOptionsWidget}
              widgetId="_drag_options"
              value=""
              onChange={() => {}}
            />
          </div>
        )}

        {showSubmitButton && (
          <button className="exercise-submit-btn" onClick={handleSubmit}>
            Check Answer
          </button>
        )}
      </div>
    </ExerciseDndProvider>
    </SoundPlayerProvider>
  );
});

ExerciseRenderer.displayName = 'ExerciseRenderer';
